import { LitElement, type TemplateResult, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { logotype, medal, trophy } from '../../svg';
import { appRouter } from '../../router';
import type { Package } from './entities';

@customElement('umb-market-package-owner-badge')
export class UmbMarketPackageOwnerBadge extends LitElement {
  @property({ type: Object })
  package?: Package;

  #setHover(e: Event) {
    const badge = this.#getBadgeElement(e);
    if (!badge) return;
    const target = badge.getAttribute('alias')?.toString();
    badge.nextElementSibling?.setAttribute('in', 'true');

    if (
      (target === 'award' || target === 'trophy') &&
      appRouter?.location.pathname.includes('/awards/')
    ) {
      this.dispatchEvent(
        new CustomEvent('confetti', { bubbles: true, composed: true })
      );
    }
  }

  // eslint-disable-next-line class-methods-use-this
  #clearHover(e: Event) {
    const badge = this.#getBadgeElement(e);
    badge?.nextElementSibling?.removeAttribute('in');
  }

  // eslint-disable-next-line class-methods-use-this
  #getBadgeElement(e: Event) {
    return (e.composedPath() as Array<HTMLElement>).find(
      x => x.nodeName === 'SPAN'
    );
  }

  #renderBadge(
    badgeName: string,
    icon: TemplateResult,
    tooltip: string,
    className?: string
  ) {
    // eslint-disable-next-line lit-a11y/mouse-events-have-key-events
    return html`<span
        alias=${badgeName}
        class="badge ${className}"
        @mouseenter=${this.#setHover}
        @mouseleave=${this.#clearHover}
        >${icon}</span
      >
      <umb-market-tooltip
        .text="${this.package?.title} ${tooltip}"
        x="right"
      ></umb-market-tooltip>`;
  }

  #renderPackageOwnerBadge() {
    if (this.package?.isHQ && this.package.isHQSupported) {
      return this.#renderBadge(
        'hq-supported',
        logotype,
        'is an Umbraco HQ-supported product'
      );
    }

    if (this.package?.isHQ && !this.package.isHQSupported) {
      return this.#renderBadge(
        'hq',
        logotype,
        `is a free, open-source ${this.package.packageType.toLowerCase()} from Umbraco HQ`
      );
    }

    return null;
  }

  #renderAwardeeBadge() {
    if (!this.package?.awards?.length) return null;

    const location = appRouter?.location;
    const idParam = location?.pathname.startsWith('/awards')
      ? location.params.id?.toString()
      : undefined;
    const hof = location?.pathname.includes('hall-of-fame');
    const year = !hof ? Number(idParam) : undefined;

    // when no year, we keep all awards
    const forYear = year
      ? this.package.awards.filter(x => x.year === year)
      : this.package.awards;

    let caption = '';
    const winner = forYear.filter(x => x.result === 'Winner');
    const finalist = forYear.filter(
      x => x.result === 'Nominee' || x.result === 'Runner-up'
    );

    if (winner.length) {
      caption = `is ${
        winner.length > 1 ? 'a multiple' : 'an'
      } award-winning Umbraco package (${winner
        .map(x => `${x.category} ${x.year}`)
        .join(', ')})`;
    } else if (finalist.length) {
      caption = `was a finalist in the Umbraco Package Awards (${finalist
        .map(x => `${x.category} ${x.year}`)
        .join(', ')})`;
    }

    let awardeeBadge;
    if (caption.length) {
      awardeeBadge = this.#renderBadge(
        'award',
        medal,
        caption,
        finalist.length && !winner.length ? 'finalist awardee' : 'awardee'
      );
    }

    const isHof = forYear.find(x => x.result === 'Hall of Fame');
    if (isHof) {
      const badges = [
        this.#renderBadge(
          'trophy',
          trophy,
          `was inducted into the Package Hall of Fame in ${isHof.year}`,
          'awardee hof'
        ),
      ];

      if (awardeeBadge) {
        badges.push(awardeeBadge);
      }

      return badges;
    }

    return [awardeeBadge];
  }

  render() {
    if (
      !this.package?.isHQ &&
      !this.package?.isPartner &&
      !this.package?.awards?.length
    ) {
      return null;
    }

    return html`<div id="badges">
      ${this.#renderAwardeeBadge()}${this.#renderPackageOwnerBadge()}
    </div>`;
  }

  static styles = [
    css`
      :host {
        --font-size: var(--badge-font-size, 12px);
        --font-weight: var(--badge-font-weight, 400);
        --logo-fill: var(--uui-color-default);

        position: relative;
      }

      #badges {
        display: flex;
      }

      umb-market-tooltip {
        position: absolute;
        inset: 0;
        pointer-events: none;
      }

      .badge {
        height: var(--badge-height, var(--l-spacer));
        display: block;
      }

      .badge:not(.awardee) svg {
        width: 100%;
        height: 100%;
      }

      .badge:not(:first-of-type) {
        margin-left: 3px;
      }

      .hof {
        --logo-fill: var(--uui-color-current-contrast);

        padding: 6px;
        box-sizing: border-box;
      }

      .awardee {
        --fill: gold;

        background-color: var(--logo-fill);
        width: var(--badge-height, var(--l-spacer));
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
      }

      .finalist {
        --fill: silver;
      }
    `,
  ];
}
